import { Theme } from "@mui/material";

const canUseSymbol = typeof Symbol === 'function' && Symbol.for;
const REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;

export function debounce<F extends (...params: any[]) => void>(fn: F, delay: number) {
    let timeoutID: number = null;
    return function (this: any, ...args: any[]) {
        clearTimeout(timeoutID);
        timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
    } as F;
}

export function nprop<T>(someObject: T, defaultValue: T = {} as T): T {
    if (typeof someObject === 'undefined' || someObject === null)
        return defaultValue;
    else
        return someObject;
}


export function combineStyles(...styles: any[]) {
    return function CombineStyles(theme: Theme) {
        const outStyles = styles.map((arg: any) => {
            // Apply the "theme" object for style functions.
            if (typeof arg === 'function') {
                return arg(theme);
            }
            // Objects need no change.
            return arg;
        });
        return outStyles.reduce((acc, val) => Object.assign(acc, val));
    };
}


export function validateEmail(email: string): boolean {
    var re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
}

export default class LibUtils {
    static is: any;

    static newGuid() {
        var d = new Date().getTime();
        if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
            d += performance.now(); //use high-precision timer if available
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        }).toUpperCase();
    }

    static isNonNullObject(value: any) {
        return !!value && typeof value === 'object';
    }

    static isValidDate(d: any) {
        return d instanceof Date && isFinite(d.getTime());
    }

    static isSpecial(value: any) {
        var stringValue = Object.prototype.toString.call(value);
        return stringValue === '[object RegExp]'
            || stringValue === '[object Date]'
            || LibUtils.isReactElement(value);
    }

    static isReactElement(value: any): boolean {
        return value.$$typeof === REACT_ELEMENT_TYPE;
    }

    static firstWord(text: string): string {
        return text.replace(/ .*/, '');
    }

    static truncateText(text: string, maxLen: number): string {
        if (text == null || text.length < maxLen) return text;
        return text.substr(0, maxLen - 1) + "...";
    }

    static removeAccents(text: string) {
        return text.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
    }

    static emptyToNull(value: string) {
        return (value == null || value.length == 0) ? null : value;
    }

    static tokenizerString(text: string, delimiters: string): string[] {
        const tokens: string[] = [];
        let str = "";

        for (var i = 0; i < text.length; i++) {
            const c = text[i];
            if (delimiters.indexOf(c) == -1) {
                str += c;
            }
            else {
                if (str != "") tokens.push(str);
                tokens.push(c);
                str = "";
            }
        }
        if (str != "") tokens.push(str);

        return tokens;
    }

    static copyProperties(source: any, destination: any, exclude: string[] = null): void {
        for (const prop in source) {
            if (exclude == null || exclude.findIndex(p => p == prop) == -1) {
                destination[prop] = source[prop];
            }
        }
    }

    static isAllPropertiesWithValue(source: any, compareValue: any, excludeFields: string[] = null): boolean {
        if (source == null)
            return compareValue === null;

        for (const prop in source) {
            if (excludeFields != null && excludeFields.findIndex(x => x == prop) > -1) continue;
            if (source[prop] != compareValue) return false;
        }

        return true;
    }

    static replaceRouterParameter(q: string, arg: Object[]): string {
        var result = q;

        var re = /(:\w*(\?)?)/gm;
        var matches = q.match(re);
        matches.forEach((v, index) => {
            if (arg[index])
                result = result.replace(v, arg[index].toString());
        });

        return result;
    }

    static removeRouterOptionalParameters(q: string): string {
        var re = /(\/:\w*(\?)?)/gi;
        return q.replace(re, "");
    }


    static isEqual(newObj: any, prevObj: any): boolean {
        if (!newObj || !prevObj) {
            return newObj === prevObj;
        }

        var isDiff =
            Object
                .keys(Object.assign({}, newObj, prevObj))
                .find((key) => newObj[key] !== prevObj[key]);

        return isDiff == null;
    }

    static isExpired(compareDate: Date, dts: Date, dte: Date) {
        if (dts == null && dte == null) return false;
        if (dts != null && dte != null && !(compareDate >= dts && compareDate <= dte)) return false;
        if (dte != null && compareDate >= dte) return false;
        return true;

    }


    static compareObjects(x: Object, y: Object): boolean {
        if (x === y) return true;
        // if both x and y are null or undefined and exactly the same

        if (!(x instanceof Object) || !(y instanceof Object)) return false;
        // if they are not strictly equal, they both need to be Objects

        if (x.constructor !== y.constructor) return false;
        // they must have the exact same prototype chain, the closest we can do is
        // test there constructor.

        for (var p in x) {
            if (!x.hasOwnProperty(p)) continue;
            // other properties were tested using x.constructor === y.constructor

            if (!y.hasOwnProperty(p)) return false;
            // allows to compare x[ p ] and y[ p ] when set to undefined

            if (x[p] === y[p]) continue;
            // if they have the same strict value or identity then they are equal

            if (typeof (x[p]) !== "object") return false;
            // Numbers, Strings, Functions, Booleans must be strictly equal

            if (!LibUtils.compareObjects(x[p], y[p])) return false;
            // Objects and Arrays must be tested recursively
        }

        for (p in y) {
            if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
            // allows x[ p ] to be set to undefined
        }
        return true;
    }

    static cleanupRouteParams(p: string): string {
        if (p == null) return p;
        var arr = p.split('/');
        return arr.filter(x => !x.startsWith(":")).join("/");
    }


    static getFileExtension(filename: string): string {
        return filename == null ? "" : filename.split('.').pop();
    }

    static deepCopy(obj: any): any {
        return JSON.parse(JSON.stringify(obj));
    }

    static deepCompare(obj1: any, obj2: any) {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }
}